import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from 'components/AppIcon';

export default function Register() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!fullName || !email || !password) {
      setError('Por favor completa todos los campos.');
      return;
    }
    if (password?.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setLoading(true);
    try {
      const { data, error: signUpError } = await signUp(email, password, fullName);
      if (signUpError) {
        setError(signUpError?.message || 'Error al crear la cuenta.');
      } else if (data?.session === null) {
        // Email confirmation required
        navigate('/email-confirmation', { replace: true, state: { email } });
      } else {
        // Session exists — email confirmation disabled, go to dashboard
        navigate('/main-dashboard', { replace: true });
      }
    } catch (err) {
      setError('Error de red. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--color-background)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: 'var(--color-accent)' }}>
            <Icon name="Shield" size={28} color="white" strokeWidth={2} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">MetalGuard</h1>
          <p className="text-muted-foreground mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>
            Gestión inteligente de chatarra metálica
          </p>
        </div>

        {/* Card */}
        <div className="card p-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-6">Crear cuenta</h2>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg mb-4 border" style={{ background: 'var(--color-error)15', borderColor: 'var(--color-error)40' }}>
              <Icon name="AlertCircle" size={16} color="var(--color-error)" strokeWidth={2} />
              <p className="text-sm" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Nombre completo
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e?.target?.value)}
                placeholder="Tu nombre"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-accent transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
                autoComplete="name"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Correo electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="tu@empresa.com"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-accent transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-accent transition-colors"
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
                  autoComplete="new-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" strokeWidth={2} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white font-semibold transition-opacity disabled:opacity-60"
              style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
            >
              {loading ? (
                <><Icon name="Loader2" size={18} color="white" strokeWidth={2} className="animate-spin" /> Creando cuenta...</>
              ) : (
                <><Icon name="UserPlus" size={18} color="white" strokeWidth={2} /> Crear cuenta</>
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <span className="text-sm text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              ¿Ya tienes cuenta?{' '}
            </span>
            <Link to="/login" className="text-sm font-medium" style={{ color: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Iniciar sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
